import React, { useCallback, useEffect, useRef } from 'react';

import { useDispatch } from 'react-redux';

import { Input } from 'shared/components/ihcl/input';
import { Input as InputV2 } from 'shared/components/ihcl/v2/input';

import { getZipCodeInfo } from '../actions/zipCodeActions';
import { Place } from '../records/Place';

type ZipCodeInputProps = {
  caption?: React.ReactNode;
  cityInputName?: string;
  error?: boolean;
  id?: string;
  label?: React.ReactNode;
  onChange: (place: Place, something: boolean) => void;
  place: Place;
  placeholder?: string;
  stateInputName?: string;
  useIhcl?: boolean;
  zipCodeInputName?: string;
};

const ZipCodeInput = ({
  caption = null,
  cityInputName = null,
  error = false,
  id = '',
  label = null,
  onChange,
  place,
  placeholder = '',
  stateInputName = null,
  useIhcl = false,
  zipCodeInputName = null,
}: ZipCodeInputProps) => {
  const dispatch = useDispatch();
  const placeRef = useRef(place);

  useEffect(() => {
    placeRef.current = place;
  }, [place]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const zipCode = e.currentTarget.value.substr(0, 5);
      if (!zipCode.match(/\d{5}/)) {
        onChange(new Place({ zipCode }), false);
        return;
      }

      const inputModifiedDuringRequest = () =>
        placeRef.current.zipCode !== zipCode;

      onChange(new Place({ zipCode }), true);
      dispatch(getZipCodeInfo(zipCode))
        .then((json) => {
          if (!inputModifiedDuringRequest()) {
            onChange(
              new Place({
                city: json.city,
                state: json.state,
                zipCode: json.zip_code,
              }),
              false
            );
          }
        })
        .catch(() => {
          if (!inputModifiedDuringRequest()) {
            onChange(new Place({ zipCode }), false);
          }
        });
    },
    [dispatch, onChange]
  );

  const { zipCode } = place;

  const hiddenInputs = [];
  if (cityInputName && place.city) {
    hiddenInputs.push(
      <input
        key="hiddenCity"
        name={cityInputName}
        type="hidden"
        value={place.city}
      />
    );
  }

  if (stateInputName && place.state) {
    hiddenInputs.push(
      <input
        key="hiddenState"
        name={stateInputName}
        type="hidden"
        value={place.state}
      />
    );
  }

  if (zipCodeInputName && place.zipCode) {
    hiddenInputs.push(
      <input
        key="hiddenZipCode"
        name={zipCodeInputName}
        type="hidden"
        value={place.zipCode}
      />
    );
  }

  if (useIhcl) {
    return (
      <div>
        <Input
          id={id.length > 0 ? id : undefined}
          onChange={handleChange}
          error={error}
          type="text"
          value={zipCode || ''}
          caption={caption}
          label={label}
        />
        {hiddenInputs}
      </div>
    );
  }

  return (
    <div>
      <InputV2
        id={id.length > 0 ? id : undefined}
        onChange={handleChange}
        type="text"
        value={zipCode || ''}
        placeholder={placeholder}
      />
      {hiddenInputs}
    </div>
  );
};

export default ZipCodeInput;
